/* eslint-disable arrow-body-style */
import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Image,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';

import { useStyles } from './style';

export const ImagePreviewView = ({
  imageView,
  url,
  buttonsConfig,
  buttonsRef,
  notificationConfig,
  permissions,
}) => {
  const classes = useStyles();
  const isAdsStudioOptimizeTabEnabled = useMemo(() => _.get(permissions, ['dynamic', 'AdsStudioOptimizeTab']), [permissions]);

  const buttonNum = buttonsConfig.length;

  const buttonItem = useCallback(({
    config, index, key, className, shape = 'square',
  }) => {
    const {
      onClick,
      icon: Icon,
      disabled,
      text,
      appearance = 'secondary',
    } = config;

    return (
      <Button
        key={`${key}-${index}`}
        className={mergeClasses('button-item', className)}
        onClick={onClick}
        icon={<Icon />}
        disabled={disabled}
        shape={shape}
        appearance={appearance}
      >
        {text}
      </Button>
    );
  }, []);

  const renderConfigButtons = () => {
    if (!isAdsStudioOptimizeTabEnabled) {
      const getClassName = (idx) => {
        if (idx === 0) {
          return 'first-button-item';
        }
        if (idx === buttonNum - 1) {
          return 'last-button-item';
        }
        return '';
      };

      return buttonsConfig.map((config, index) => (
        buttonItem({
          config, index, key: 'image-preview-button', className: getClassName(index),
        })
      ));
    }

    const applyButtonConfig = buttonsConfig.filter(config => config.isApplyButton)[0];
    const cancelButtonConfig = buttonsConfig.filter(config => config.isCancelButton)[0];
    const newApplyButtonConfig = applyButtonConfig ? { ...applyButtonConfig, appearance: 'primary' } : null;
    const newCancelButtonConfig = cancelButtonConfig ? { ...cancelButtonConfig, text: '' } : null;
    const otherButtonConfigList = buttonsConfig.filter(config => !config.isApplyButton && !config.isCancelButton);
    const showDivider = !_.isEmpty(otherButtonConfigList) && (newApplyButtonConfig || newCancelButtonConfig);

    return (
      <div className="button-config-wrapper">
        {
          otherButtonConfigList.map((config, index) => (
            buttonItem({
              config, index, key: 'image-preview-button', shape: 'rounded',
            })
          ))
        }
        {showDivider && <div className="button-divider" />}
        {newApplyButtonConfig && buttonItem({
          config: newApplyButtonConfig, index: buttonNum - 2, key: 'image-preview-button', shape: 'rounded', className: 'apply-button',
        })}
        {newCancelButtonConfig && buttonItem({
          config: newCancelButtonConfig, index: buttonNum - 1, key: 'image-preview-button', shape: 'rounded', className: 'cancel-button',
        })}
      </div>
    );
  };

  const notificationContent = useMemo(() => {
    const {
      icon,
      content,
      onDismiss,
      dismissAriaLabel,
    } = notificationConfig || {};

    return (
      <div className="image-preview-notification-wrapper">
        {icon}
        {content}
        {
          _.isFunction(onDismiss) &&
          <Button
            icon={<Dismiss20Regular />}
            appearance="subtle"
            onClick={onDismiss}
            aria-label={dismissAriaLabel}
          />
        }
      </div>
    );
  }, [notificationConfig]);

  return (
    <div className={mergeClasses(classes.imagePreviewView, 'image-preview-view')}>
      {
        buttonNum > 0 &&
        <div className="image-preview-button-wrapper" ref={buttonsRef}>
          {renderConfigButtons()}
        </div>
      }
      <div className="image-preview-image-wrapper">
        {
          imageView ||
          <Image
            className="image-item"
            src={url}
            alt="image preview"
          />
        }
      </div>
      {
        notificationConfig && notificationContent
      }
    </div>
  );
};

ImagePreviewView.propTypes = {
  imageView: PropTypes.shape({}),
  url: PropTypes.string,
  buttonsConfig: PropTypes.arrayOf(PropTypes.shape({})),
  buttonsRef: PropTypes.shape({}),
  notificationConfig: PropTypes.shape({}),
};

ImagePreviewView.defaultProps = {
  imageView: undefined,
  url: undefined,
  buttonsConfig: [],
  buttonsRef: undefined,
  notificationConfig: undefined,
};
